// frontend/src/components/client/CaseLookup.js
// Public page — no auth required.
// Mount in your router as: <Route path="/case-lookup" element={<CaseLookup />} />

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, ArrowRightIcon, ExclamationTriangleIcon, DocumentTextIcon, ScaleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

// ── Config ────────────────────────────────────────────────────────────────────
// Adjust these to match your environment / api service file
const API_BASE_URL  = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const CLIENT_URL    = process.env.REACT_APP_CLIENT_URL || window.location.origin;

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const statusColor = {
  Open:         'text-green-700  bg-green-50  border-green-200',
  'In Progress':'text-blue-700   bg-blue-50   border-blue-200',
  'Under Review':'text-yellow-700 bg-yellow-50 border-yellow-200',
  Closed:       'text-gray-600   bg-gray-100  border-gray-200',
  Pending:      'text-orange-700 bg-orange-50 border-orange-200',
};

const priorityColor = {
  Low:    'text-gray-500',
  Medium: 'text-blue-600',
  High:   'text-orange-600',
  Urgent: 'text-red-600 font-bold',
};

// ── Component ─────────────────────────────────────────────────────────────────
const CaseLookup = () => {
  const navigate   = useNavigate();
  const inputRef   = useRef(null);

  const [query,   setQuery]   = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);   // { caseId, caseNumber, adminId, charges, … }
  const [error,   setError]   = useState('');

  // ── Lookup handler ──────────────────────────────────────────────────────────
  const handleLookup = async (e) => {
    e.preventDefault();
    const trimmed = query.trim().toUpperCase();

    if (!trimmed || trimmed.length < 3) {
      toast.error('Please enter a valid case number.');
      inputRef.current?.focus();
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const { data } = await axios.get(`${API_BASE_URL}/lookup/case`, {
        params: { caseNumber: trimmed }
      });

      if (data.success) {
        setResult(data.data);
      } else {
        setError(data.message || 'Case not found.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to complete lookup. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Sign-up redirect ────────────────────────────────────────────────────────
  const handleSignUp = () => {
    if (!result) return;
    const signupLink = `/signup?ref=${result.adminId}&case=${result.caseId}`;
    navigate(signupLink);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header banner ── */}
      <div className="bg-[#1a2744] text-white py-10 px-4 text-center">
        <div className="flex justify-center mb-3">
          <ScaleIcon className="w-10 h-10 text-[#c9a84c]" />
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-widest uppercase mb-2"
          style={{ fontFamily: '"Times New Roman", serif', letterSpacing: '0.15em' }}
        >
          Case Lookup
        </h1>
        <p className="text-sm text-gray-300 max-w-md mx-auto">
          Enter your case number to view case details, associated charges, and create your account.
        </p>
      </div>

      {/* ── Search form ── */}
      <div className="max-w-2xl mx-auto px-4 -mt-6">
        <form
          onSubmit={handleLookup}
          className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
        >
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Case Number
          </label>
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. BTA-25-1750"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-[#1a2744] focus:border-transparent placeholder:normal-case placeholder:font-sans placeholder:tracking-normal"
              disabled={loading}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-[#1a2744] hover:bg-[#243560] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg text-sm transition-colors whitespace-nowrap"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching…
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-4 h-4" />
                  Look Up
                </>
              )}
            </button>
          </div>

          {/* Already have an account link */}
          <p className="mt-3 text-xs text-gray-500 text-center">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-[#1a2744] font-semibold hover:underline"
            >
              Sign in here
            </button>
          </p>
        </form>
      </div>

      {/* ── Error state ── */}
      {error && (
        <div className="max-w-2xl mx-auto px-4 mt-5">
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-5 text-red-700">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Case Not Found</p>
              <p className="text-sm mt-0.5">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Result card ── */}
      {result && (
        <div className="max-w-2xl mx-auto px-4 mt-5 pb-16">

          {/* Case header card */}
          <div
            className="rounded-xl shadow-md border-t-4 bg-white overflow-hidden mb-4"
            style={{ borderColor: result.displayColor || '#1a2744' }}
          >
            {/* Case identity */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1 font-mono">
                    {result.caseNumber}
                  </p>
                  <h2
                    className="text-xl font-bold text-gray-900 uppercase"
                    style={{ fontFamily: '"Times New Roman", serif' }}
                  >
                    {[result.firstName, result.lastName].filter(Boolean).join(' ') || 'Unnamed Case'}
                  </h2>
                  {result.description && (
                    <p className="text-sm text-gray-500 mt-1">{result.description}</p>
                  )}
                </div>

                {/* Status badge */}
                <span
                  className={`text-xs font-bold uppercase px-3 py-1 rounded-full border ${
                    statusColor[result.status] || 'text-gray-600 bg-gray-100 border-gray-200'
                  }`}
                >
                  {result.status}
                </span>
              </div>

              {/* Meta row */}
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                {/*<span>
                  Type:{' '}
                  <span className="font-semibold text-gray-700">{result.caseType}</span>
                </span>*/}
                <span>
                  Status:{' '}
                  <span className="font-semibold text-red-700">Active</span>
                </span>
                <span>
                  Priority:{' '}
                  <span className={`font-semibold ${priorityColor[result.priority] || ''}`}>
                    {result.priority}
                  </span>
                </span>
                {result.dueDate && (
                  <span>
                    Due:{' '}
                    <span className="font-semibold text-gray-700">
                      {new Date(result.dueDate).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Notice */}
            <div className="bg-amber-50 border-b border-amber-100 px-6 py-3">
              <p className="text-xs text-amber-800 leading-relaxed">
                <span className="font-bold text-red-600">NOTICE: </span>
                This case has pending citations. To resolve this matter, please create an account
                using the button below or contact the Civil Services Division directly.
              </p>
            </div>

            {/* Charges table */}
            <div className="px-6 py-4">
              <div className="flex items-center gap-2 mb-3">
                <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Associated Charges
                </span>
              </div>

              {result.charges && result.charges.length > 0 ? (
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a2744] text-white">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold tracking-wider">Statute</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold tracking-wider">Charge</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold tracking-wider">Fine</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {result.charges.map((charge, idx) => (
                        <tr
                          key={charge._id}
                          className={idx % 2 === 0 ? 'bg-yellow-50' : 'bg-white'}
                        >
                          <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                            {charge.sectionNumber || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{charge.chargeName}</p>
                            {charge.chargeDescription && (
                              <p className="text-xs text-gray-500 mt-0.5">{charge.chargeDescription}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {formatCurrency(charge.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic py-2">
                  No charges attached to this case.
                </p>
              )}
            </div>

            {/* Total + CTA */}
            <div className="px-6 pb-6">
              <div className="flex items-center justify-between py-3 border-t border-gray-200 mb-5">
                <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Total Outstanding</span>
                <span className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: '"Times New Roman", serif' }}>
                  {formatCurrency(result.totalCharges)}
                </span>
              </div>

              {/* Sign-up CTA */}
              <div className="bg-[#f0f4f8] rounded-xl p-5 text-center">
                <p className="text-sm text-gray-600 mb-1 font-medium">
                  Create an account to manage this case online
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  Your case will be automatically linked to your new account.
                </p>
                <button
                  onClick={handleSignUp}
                  className="inline-flex items-center gap-2 bg-[#1a2744] hover:bg-[#243560] text-white font-bold px-8 py-3 rounded-lg text-sm transition-colors shadow-md w-full sm:w-auto justify-center"
                >
                  Create Account &amp; Access Case
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
                <p className="text-xs text-gray-400 mt-3">
                  Already registered?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-[#1a2744] font-semibold hover:underline"
                  >
                    Sign in instead
                  </button>
                </p>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default CaseLookup;